/**
 * SpeechDictation - Speech-to-Text Dictation Component
 * 
 * A reusable component that adds voice dictation capability to any text input.
 * Used throughout patient documentation and notes.
 * 
 * Features:
 * - Real-time speech-to-text transcription
 * - Continuous dictation mode
 * - Visual feedback while listening
 * - Auto-punctuation suggestions
 * - Multi-language support
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { Mic, MicOff, Volume2, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function SpeechDictation({
  onTranscript,
  onInterimTranscript,
  continuous = true,
  language = "en-US",
  className = "",
  buttonVariant = "outline",
  buttonSize = "sm",
  showStatus = true,
  disabled = false
}) {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [interimText, setInterimText] = useState("");
  const [error, setError] = useState(null);
  const recognitionRef = useRef(null);

  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      setIsSupported(false);
      return;
    }

    setIsSupported(true);
    const recognition = new SpeechRecognition();
    recognition.continuous = continuous;
    recognition.interimResults = true;
    recognition.lang = language;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      setError(null);
    };

    recognition.onend = () => {
      setIsListening(false);
      setInterimText("");
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
      setIsListening(false);
      setError(event.error);
      
      if (event.error === "not-allowed") {
        toast.error("Microphone access denied. Please enable it in your browser settings.");
      } else if (event.error === "no-speech") {
        toast.info("No speech detected. Try again.");
      } else {
        toast.error(`Speech recognition error: ${event.error}`);
      }
    };

    recognition.onresult = (event) => {
      let finalTranscript = "";
      let interimTranscript = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      // Update interim text for visual feedback
      setInterimText(interimTranscript);
      if (onInterimTranscript) {
        onInterimTranscript(interimTranscript);
      }

      // Send final transcript
      if (finalTranscript && onTranscript) {
        // Add basic punctuation improvements
        let processedText = finalTranscript;
        processedText = processedText.replace(/\bperiod\b/gi, ".");
        processedText = processedText.replace(/\bcomma\b/gi, ",");
        processedText = processedText.replace(/\bquestion mark\b/gi, "?");
        processedText = processedText.replace(/\bnew line\b/gi, "\n");
        processedText = processedText.replace(/\bnew paragraph\b/gi, "\n\n");
        
        onTranscript(processedText);
      }
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, [continuous, language, onTranscript, onInterimTranscript]);

  const toggleListening = useCallback(() => {
    if (!isSupported) {
      toast.error("Speech recognition is not supported in your browser.");
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
    } else {
      try {
        recognitionRef.current?.start();
        toast.info("🎤 Listening... Speak clearly into your microphone.");
      } catch (err) {
        console.error("Failed to start speech recognition:", err);
        toast.error("Failed to start speech recognition. Please try again.");
      }
    }
  }, [isListening, isSupported]);

  if (!isSupported) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size={buttonSize}
              disabled
              className={`text-muted-foreground ${className}`}
            >
              <MicOff className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Speech recognition not supported in this browser</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={isListening ? "destructive" : buttonVariant}
              size={buttonSize}
              onClick={toggleListening}
              disabled={disabled}
              className={`relative ${isListening ? "animate-pulse" : ""}`}
            >
              {isListening ? (
                <>
                  <MicOff className="w-4 h-4 mr-1" />
                  Stop
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping" />
                </>
              ) : (
                <>
                  <Mic className="w-4 h-4 mr-1" />
                  Dictate
                </>
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{isListening ? "Click to stop dictation" : "Click to start voice dictation"}</p>
            <p className="text-xs text-muted-foreground">
              Say "period", "comma", "new line" for punctuation
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {showStatus && isListening && (
        <Badge variant="secondary" className="animate-pulse flex items-center gap-1">
          <Volume2 className="w-3 h-3" />
          Listening...
        </Badge>
      )}

      {showStatus && interimText && (
        <span className="text-sm text-muted-foreground italic truncate max-w-[200px]">
          "{interimText}"
        </span>
      )}

      {showStatus && error && (
        <Badge variant="destructive" className="flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          Error
        </Badge>
      )}
    </div>
  );
}

/**
 * useSpeechDictation - Hook for speech dictation
 * 
 * Use this hook to add dictation to any textarea or input field.
 */
export function useSpeechDictation(options = {}) {
  const {
    onTranscript,
    continuous = true,
    language = "en-US"
  } = options;

  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [transcript, setTranscript] = useState("");
  const recognitionRef = useRef(null);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      setIsSupported(false);
      return;
    }

    setIsSupported(true);
    const recognition = new SpeechRecognition();
    recognition.continuous = continuous;
    recognition.interimResults = false;
    recognition.lang = language;

    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);

    recognition.onresult = (event) => {
      const result = event.results[event.results.length - 1][0].transcript;
      setTranscript(prev => prev + " " + result);
      if (onTranscript) {
        onTranscript(result);
      }
    };

    recognitionRef.current = recognition;

    return () => recognitionRef.current?.abort();
  }, [continuous, language, onTranscript]);

  const startListening = useCallback(() => {
    if (isSupported && !isListening) {
      recognitionRef.current?.start();
    }
  }, [isSupported, isListening]);

  const stopListening = useCallback(() => {
    if (isListening) {
      recognitionRef.current?.stop();
    }
  }, [isListening]);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  const clearTranscript = useCallback(() => {
    setTranscript("");
  }, []);

  return {
    isListening,
    isSupported,
    transcript,
    startListening,
    stopListening,
    toggleListening,
    clearTranscript
  };
}

/**
 * DictationTextarea - Textarea with built-in dictation
 * 
 * A ready-to-use textarea component with speech dictation.
 */
export function DictationTextarea({
  value,
  onChange,
  placeholder = "Type or use voice dictation...",
  rows = 4,
  className = "",
  disabled = false,
  ...props
}) {
  const handleTranscript = useCallback((text) => {
    const newValue = value ? `${value} ${text}` : text;
    onChange?.({ target: { value: newValue } });
  }, [value, onChange]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <SpeechDictation
          onTranscript={handleTranscript}
          disabled={disabled}
          buttonSize="sm"
          showStatus={true}
        />
      </div>
      <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        className={`w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
        {...props}
      />
      <p className="text-xs text-muted-foreground">
        💡 Tip: Click "Dictate" and speak. Say "period", "comma", or "new line" for punctuation.
      </p>
    </div>
  );
}
